import React, { useEffect, useState, useRef } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { URL_LOGIN } from "@components/Routes/Location";
import AlertNotification from "@components/GUI/Alert/Alert";
import CircularProgress from "@material-ui/core/CircularProgress";
import ErrorPlayerModalComponent from "./ErrorPlayerModalComponent";
import "./Player.css";
import QRModalComponent from "../../../../app/src/Components/common/QRModalComponent";
import {
  saveContentHashActionCreator, saveContentPermissionsActionCreator,
  saveContentTypeActionCreator,
  turnOffLoadingActionCreator,
  turnOnLoadingActionCreator,
} from "../../../../app/src/actions/commonActions";
import ErrorMessageModalComponent from "../../../../app/src/Components/common/ErrorMessageModalComponent";
import { getAnonContentsService, getContentPermissions, getContentsService } from "../../../../app/src/services/services";
import {
  TENANT,
} from "../../../../app/src/Resources/Multitenant/tenantConstants";
import { useKeycloak } from "@react-keycloak/web";
import {
  URL_BASE,
  URL_BASE_V2,
} from "../../../../app/src/services/servicesConstants";
import {getToken} from "../../../../app/src/Utils/utils";
import i18n from "../../../../app/src/i18n";
import { isCordovaAvailable } from "../../../../app/src/Utils/secureStorageUtil";
import ShareContentModalComponent from "../../../../detail/src/Pages/DetailPage/ShareContentModalComponent";
import { useTranslation } from "react-i18next";
import ScriptEmbed from "../../../../detail/src/Pages/DetailPage/Widgets/ScriptEmbed";
import { PLAYER_VERSION_LEGACY } from "../../../../app/src/Utils/Constants";

function Player({ id }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const history = useHistory();
  const userToken = getToken(keycloak);
  const baseUrl = URL_BASE;

  const contentHash = useSelector((state) => state.commonState.contentHash);
  const contentType = useSelector((state) => state.commonState.contentType);
  const { content: contentRedux, contentPermissions } = useSelector((state) => state.commonState);
  const languageSelected = useSelector((state) => state.commonState.languageSelected) || i18n.language;
  const playerVersion = useSelector((state) => state.commonState.playerVersion);
  // El tenant indica en /interfaz qué player usa: "v1" el antiguo (iframe), "v2" el nuevo (embed.js).
  // Hasta que /interfaz responde no montamos ningún player para no cargar el que no toca.
  const isPlayerVersionResolved = !!playerVersion;
  const isLegacyPlayer = playerVersion === PLAYER_VERSION_LEGACY;
  const username = keycloak?.tokenParsed?.preferred_username;
  const query = new URLSearchParams(window.location.search);
  const payload = query.get("payload") || false;

  const [content, setContent] = useState(null);
  const [player, setPlayer] = useState(null);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(!isCordovaAvailable());
  const [timeoutError, setTimeoutError] = useState(false);
  const [errorUserSessionExpired, setErrorUserSessionExpired] = useState(false);
  const [errorVideoUrlUnavailable, setErrorVideoUrlUnavailable] =
    useState(false);
  const [displayQRModal, setDisplayQRModal] = useState(false);
  const [displayShareContentModal, setDisplayShareContentModal] = useState(false);
  const [messageModal, setMessageModal] = useState(false);
  const [hash, setHash] = useState(null);
  const [alloweb, setAllowed] = useState(false);
  const iframeRef = useRef(null);
  const refShare = useRef(null);

  const turnOnLoading = () => {
    dispatch(turnOnLoadingActionCreator());
  };

  const turnOffLoading = () => {
    dispatch(turnOffLoadingActionCreator());
  };

  const contentPermissionsCallback = (response, contentType) => {
    dispatch(saveContentPermissionsActionCreator({response, contentType, id}));
    setAllowed(response.allowed);
    if(response?.hashRequired) dispatch(saveContentHashActionCreator(response.hash));
  };

  useEffect(() => {
    if (!contentHash || !contentPermissions || contentPermissions.id !== id) {
      getContentPermissions(TENANT, id, userToken, contentType, (response, type) => contentPermissionsCallback(response, type));
    } else {
      contentPermissionsCallback(contentPermissions.response, contentPermissions.contentType);
    }
    return () => {
        dispatch(saveContentHashActionCreator(null));
        dispatch(saveContentTypeActionCreator(null));
        dispatch(saveContentPermissionsActionCreator(null));
    };
  }, []);

  const getContentsCallback = (response) => {
    const responseContent = response.data;
    setContent({
      content: responseContent,
    });
  };

  const getAnonContentsCallback = (response) => {
    const responseContent = response?.data;
    setContent({
      content: responseContent,
    });
  };

  useEffect(() => {
    if (!!username) {
      if (!!contentRedux && contentRedux?.id === id) {
        setContent({
          content: contentRedux,
        });
      } else {
        getContentsService(TENANT, id, userToken, payload, getContentsCallback);
      }
    } else {
      getAnonContentsService(TENANT, id, payload, getAnonContentsCallback);
    }
  }, [id, languageSelected, contentRedux]);

  useEffect(() => {
    if (!isPlayerVersionResolved) {
      return;
    }

    const formattedModel = {};
    turnOnLoading();
    const availableHash = !!hash ? hash : contentHash;
    let videoUrl = isLegacyPlayer
      ? `${baseUrl}player/${TENANT}/${id}`
      : `${URL_BASE_V2}player/${TENANT}/embed/${id}/embed.js`;

    if (isLegacyPlayer && isCordovaAvailable()) {
      videoUrl = `${videoUrl}?device=android&`;
    }

    const params = new URLSearchParams();
    if (!!userToken) {
      params.append('token', userToken);
    }
    if (!!availableHash) {
      params.append('hash', availableHash);
    }
    const currentUrlParams = new URLSearchParams(window.location.search);
    currentUrlParams.forEach((value, key) => {
      params.append(key, value);
    });
    if (!!params.toString()) {
      videoUrl = `${videoUrl}?${params.toString()}`;
    }

    formattedModel.videoUrl = videoUrl;
    formattedModel.contentType = contentType;

    setPlayer(formattedModel);
  }, [hash, userToken, contentHash, isLegacyPlayer, isPlayerVersionResolved]);

  useEffect(() => {
    if (!!player?.videoUrl) {
      turnOffLoading();
    }
  }, [player]);

  const handleCloseQRModal = () => {
    setDisplayQRModal((displayQRModal) => !displayQRModal);
  };

  useEffect(() => {
    if (!isPlayerVersionResolved || !isLegacyPlayer) {
      return;
    }

    const timeout = setTimeout(() => {
      if (isLoading) {
        setTimeoutError(true);
        setIsLoading(false);
      }
    }, 20000);

    return () => clearTimeout(timeout);
  }, [isLoading, isLegacyPlayer, isPlayerVersionResolved]);

  const actionToLoaded = () => {
    setTimeoutError(false);
    setIsLoading(false)
  }

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.onload = () => actionToLoaded();
      iframeRef.current.onerror = () => {
        setIsLoading(false);
        setError(true);
        setTimeoutError(true);
      };
    }
  }, [player?.videoUrl]);

  useEffect(() => {
    if (!isLegacyPlayer) {
      return;
    }

    const handleMessage = (event) => {
      if (event.data.type === 'return') {
        if (event.data.message === true) {
          history.goBack();
        }
      }
      if (event.data.type === 'compartir') {
        if (event.data.message === true) {
          setDisplayShareContentModal(true);
        }
      }
      if (event.data.type === 'redirect') {
        if (!!event?.data?.message) {
          window.cordova.InAppBrowser.open(event.data.message, "_system");
        }
      }
      if (event.data === "Iniciada comunicación") {
        sendAutoplay();
      }
    };
    const sendAutoplay = () => {
      iframeRef.current?.contentWindow.postMessage(
        { type: "allowAudioAutoplay" },
        "*"
      );
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isLegacyPlayer]);

  const hideShareModal = () => {
    setDisplayShareContentModal(false);
  };

  return (
    <>
      {isPlayerVersionResolved && isLegacyPlayer && isLoading && (
        <div className="spinner">
          <CircularProgress size={50} thickness={4} style={{ color: "var(--accentColor)" }} />
        </div>
      )}
      {isLegacyPlayer && timeoutError && (
        <ErrorPlayerModalComponent
          refModal={null}
          handleCloseModal={() => setTimeoutError(false)}
        />
      )}
        <div className={"Player"}>
        {!isPlayerVersionResolved ? null : !isLegacyPlayer ? (
          player?.videoUrl && (
            <ScriptEmbed src={player.videoUrl} width="100%" height="100%" />
          )
        ) : isCordovaAvailable() ? (
          <ScriptEmbed id={id} />
            ) : (
          player?.videoUrl && (
              <iframe
                  ref={iframeRef}
                  src={player.videoUrl}
                  className="PlayerIframe"
                  allow="autoplay; encrypted-media; fullscreen; keyboard *"
                  id="player"
                  title={t("player.iframe.title")}
                  width="100%"
                  height="100%"
                  style={{ position: "relative", display: isLoading ? "none" : "initial" }}
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                  allowfullscreen="true"
              />
            )
          )}
        </div>

      {messageModal && <ErrorMessageModalComponent onClose={() => setMessageModal(false)} />}
      {displayQRModal && player?.shareUrl && (
        <QRModalComponent
          url={player.shareUrl}
          goBack={true}
          handleCloseQRModal={handleCloseQRModal}
        />
      )}
      {displayShareContentModal && (
            <ShareContentModalComponent
              hideShareModal={hideShareModal}
              refShare={refShare}
              title={content?.content?.title}
              description={content?.content?.description}
            />
      )}
      {errorUserSessionExpired && <Redirect to={URL_LOGIN} />}
      {!error && errorVideoUrlUnavailable && <AlertNotification />}
    </>
  );
}

export default Player;
